import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { QueryParams } from 'features/searchMolecules/searchMoleculesSlice';

// Types
export interface WorkingQuery {
  molFile?: string;
  queryParams: QueryParams;
  shouldJSMEUpdate: boolean;
}

type Param = { name: string; value: number | null };

export interface WorkingQueryState {
  queryParams: QueryParams;
  shouldJSMEUpdate: boolean;
  molFile?: string;
}

export type ChangeWorkingQueryMolFilePayload = Pick<
  WorkingQueryState,
  'molFile' | 'shouldJSMEUpdate'
>;

// Constants
export const initialQueryparams: QueryParams = {
  hac: 3,
  rac: 1,
  hops: 2,
  groupLimit: 25,
};

const defaultQueryParams: QueryParams = {
  ...initialQueryparams,
  groupLimit: 25,
};

// Slice

const initialState: WorkingQueryState = {
  queryParams: defaultQueryParams,
  shouldJSMEUpdate: false,
  molFile: undefined,
};

export const workingQuerySlice = createSlice({
  name: 'workingQuery',
  initialState,
  reducers: {
    changeWorkingQueryMolFile(state, action: PayloadAction<ChangeWorkingQueryMolFilePayload>) {
      state.molFile = action.payload.molFile;
      state.shouldJSMEUpdate = action.payload.shouldJSMEUpdate;
    },
    changeWorkingQueryParam(state, action: PayloadAction<Param>) {
      state.queryParams[action.payload.name] = action.payload.value;
    },
    changeWorkingQueryParams(state, action: PayloadAction<QueryParams>) {
      state.queryParams = action.payload;
    },
    resetWorkingQueryParams(state) {
      state.queryParams = { ...defaultQueryParams };
    },
  },
});

// Selectors

// get the auth slice form the RootState
const workingQueryStateSelector = (state: RootState, ..._: any): WorkingQueryState =>
  state.workingQuery;

export const getWorkingQuery = createSelector(workingQueryStateSelector, (state) => state);
export const getMolFile = createSelector(workingQueryStateSelector, (state) => state.molFile);
export const getQueryParams = createSelector(
  workingQueryStateSelector,
  (state) => state.queryParams,
);
export const getQueryParam = createSelector(
  workingQueryStateSelector,
  (_: RootState, property: string) => property,
  (state, property) => state.queryParams[property],
);

// Extract the action creators object and the reducer
export const { actions, reducer } = workingQuerySlice;

export const {
  changeWorkingQueryMolFile,
  changeWorkingQueryParam,
  changeWorkingQueryParams,
  resetWorkingQueryParams,
} = workingQuerySlice.actions;

// Export the reducer, either as a default export
export default workingQuerySlice.reducer;
